import type {
    MutableInternalInputPotential,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type {
    ConnectorItemDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    buildWorkflowStateEntryConnectorCollectionKey,
    buildWorkflowStateEntryItemKey,
    type WorkflowKeyScope,
} from '@/workflow/_pure/keyCodec';
import type {
    ResolveSourceRoleDirectionAndValueInput,
    SourceRoleDirectionAndValue,
    WorkflowStateEntryConnectorEndpointPair,
    WorkflowStateEntryConnectorEndpointPairInput,
} from '@/workflow/_contract/kernel';

export function tryReadInternalInputPotentialSourceAddress(args: {
    entry: unknown;
}): MutableInternalInputPotential['toolStepRoleAddress'] | null {
    const { entry } = args;
    if (!entry || typeof entry !== 'object') return null;

    const maybeEntry = entry as Record<string, unknown>;
    if (maybeEntry.workflowStateEntryKind !== 'internalInputPotential') return null;
    if (!maybeEntry.toolStepRoleAddress || typeof maybeEntry.toolStepRoleAddress !== 'object') return null;

    const toolStepRoleAddress = maybeEntry.toolStepRoleAddress as Record<string, unknown>;
    if (typeof toolStepRoleAddress.stepKey !== 'string') return null;
    if (typeof toolStepRoleAddress.roleName !== 'string') return null;

    return {
        stepKey: toolStepRoleAddress.stepKey as MutableInternalInputPotential['toolStepRoleAddress']['stepKey'],
        roleName: toolStepRoleAddress.roleName as MutableInternalInputPotential['toolStepRoleAddress']['roleName'],
    };
}

export function resolveSourceRoleDirectionAndValue(
    args: ResolveSourceRoleDirectionAndValueInput
): SourceRoleDirectionAndValue | null {
    const { tool, roleName } = args;
    const outputRoleByName = tool.roleSpec.outputRoleByName as Record<string, unknown>;
    const inputRoleByName = tool.roleSpec.inputRoleByName as Record<string, unknown>;

    if (Object.prototype.hasOwnProperty.call(outputRoleByName, roleName)) {
        return {
            direction: 'output',
            role: outputRoleByName[roleName],
        };
    }

    if (Object.prototype.hasOwnProperty.call(inputRoleByName, roleName)) {
        return {
            direction: 'input',
            role: inputRoleByName[roleName],
        };
    }

    return null;
}

export function deriveWorkflowStateEntryConnectorEndpointPair(
    args: WorkflowStateEntryConnectorEndpointPairInput & WorkflowKeyScope
): WorkflowStateEntryConnectorEndpointPair | null {
    const {
        targetStepKey,
        targetRoleName,
        targetResourceTypeHandle,
        sourceStepKey,
        sourceRoleName,
        sourceDirection,
        sourceResourceTypeHandle,
        workflowStateEntryItemKeySet,
    } = args;

    const targetItemKey = buildWorkflowStateEntryItemKey({
        stepKey: targetStepKey,
        direction: 'input',
        resourceTypeHandle: targetResourceTypeHandle,
        roleName: targetRoleName,
    });
    const sourceItemKey = buildWorkflowStateEntryItemKey({
        stepKey: sourceStepKey,
        direction: sourceDirection,
        resourceTypeHandle: sourceResourceTypeHandle,
        roleName: sourceRoleName,
    });

    if (!workflowStateEntryItemKeySet.has(targetItemKey) || !workflowStateEntryItemKeySet.has(sourceItemKey)) {
        return null;
    }

    const collectionKey = buildWorkflowStateEntryConnectorCollectionKey({
        workflowHandle: args.workflowHandle,
        threadIndex: args.threadIndex,
        resourceTypeHandle: targetResourceTypeHandle,
    });

    return {
        targetItemKey,
        sourceItemKey,
        collectionKey,
    };
}

export function appendConnectorItemDerivationToCollectionGroup(args: {
    groupedItemDerivationsByCollectionKey: Map<ItemCollection<'connector'>['itemCollectionKey'], ConnectorItemDerivation[]>;
    collectionKey: ItemCollection<'connector'>['itemCollectionKey'];
    connectorItemDerivation: ConnectorItemDerivation;
}): void {
    const { groupedItemDerivationsByCollectionKey, collectionKey, connectorItemDerivation } = args;
    const current = groupedItemDerivationsByCollectionKey.get(collectionKey) ?? [];
    current.push(connectorItemDerivation);
    groupedItemDerivationsByCollectionKey.set(collectionKey, current);
}